import { CalendarRange, Repeat, Clock, Timer } from "lucide-react";
import { ActivityCard, ItineraryItem } from "./ActivityCard";
import {
  formatSessionDateSpan,
  formatSessionSummary,
  formatSessionTiming,
  totalSessionHours,
} from "@/lib/ongoingProgrammes";

interface OngoingProgrammesSectionProps {
  items: ItineraryItem[];
}

/**
 * "Academic & Ongoing Programmes" block — recurring multi-session courses
 * (e.g. Mon–Fri language classes) presented above the day-by-day itinerary.
 */
export function OngoingProgrammesSection({ items }: OngoingProgrammesSectionProps) {
  if (!items || items.length === 0) return null;

  return (
    <div className="rounded-lg overflow-hidden border bg-background">
      <div className="bg-secondary text-secondary-foreground px-6 py-4">
        <h3 className="text-xl font-bold tracking-wide">ACADEMIC &amp; ONGOING PROGRAMMES</h3>
        <p className="text-secondary-foreground/80 text-sm">
          Runs across multiple days alongside the daily itinerary
        </p>
      </div>
      <div className="p-4 space-y-4">
        {items.map((item) => {
          const span = formatSessionDateSpan(item as any);
          const summary = formatSessionSummary(item as any);
          const timing = formatSessionTiming(item as any);
          const hours = totalSessionHours(item as any);
          return (
            <div key={item.id} className="space-y-2">
              <ActivityCard item={item} />
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-1.5">
                {span && (
                  <div className="flex items-center gap-2 text-sm">
                    <CalendarRange className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                    <span className="font-medium">{span}</span>
                  </div>
                )}
                {summary && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Repeat className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                    <span>{summary}</span>
                  </div>
                )}
                {timing && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                    <span>{timing}</span>
                  </div>
                )}
                {hours && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Timer className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                    <span>{hours} hours in total</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
